/**
 * Latency of one LiveKit pose session, timed on this device's clock and posted once to the backend
 * (POST /api/pose/latency/) when the runtime is disposed. Every function is a no-op without a probe.
 *
 *   pose_rtt  capture (frame-metadata stamp) → result here     pod      arrival at the pod → result
 *   network   pose_rtt − pod (encode, uplink, SFU, downlink)   model    model time on the pod
 *   relay     result here → sent to the backend                ws_rtt   evaluation sent → its reply
 *   e2e       capture → the backend's reply to that pose
 */

import { postLatencySummary } from '../apis/pose/api';
import { getSessionTokenFromUrl } from '../utilis/session-token';

const MAX_SAMPLES = 20000; // ~22 min at 15 fps
const MAX_PENDING = 200; // evaluations awaiting a reply
const ID_PREFIX = 'lp-';
const REPLY_ID = /"id": ?"lp-(\d+)"/;
const SERIES = ['pose_rtt', 'pod', 'model', 'network', 'relay', 'ws_rtt', 'e2e'];

let probe = null;

const plausible = ms => Number.isFinite(ms) && ms >= 0 && ms < 60000;

function add(name, ms) {
  const series = probe.series[name];
  if (plausible(ms) && series.length < MAX_SAMPLES) {
    series.push(ms);
  }
}

function spread(values) {
  if (!values.length) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const at = q => Math.round(sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))]);
  return {
    n: sorted.length,
    p50: at(0.5),
    p90: at(0.9),
    p95: at(0.95),
    max: Math.round(sorted.at(-1)),
  };
}

export function startProbe(sessionId) {
  probe = {
    sessionId,
    // Read now: the summary is posted after the exercise page, whose url carries the token, is left.
    sessionToken: getSessionTokenFromUrl(),
    startedAt: Date.now(),
    startup: {},
    series: Object.fromEntries(SERIES.map(name => [name, []])),
    pending: new Map(), // id → { sentAt, captureMs }
    nextId: 1,
    lastSentCapture: null,
    results: 0,
    heartbeats: 0,
    frame: '',
  };
  // A closed tab, a reload or a crash never disposes the runtime: send the summary as the page goes.
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', finishProbe, { once: true });
  }
}

/** A startup step, as ms since the probe started. */
export function markStartup(step) {
  if (probe && !(step in probe.startup)) {
    probe.startup[step] = Date.now() - probe.startedAt;
  }
}

/** A `model-result` / `heartbeat` from the pose service, as it arrives. */
export function onServiceMessage(message) {
  if (!probe) {
    return;
  }
  if (message.type === 'heartbeat') {
    probe.heartbeats += 1;
    return;
  }
  probe.results += 1;
  markStartup('first_result');
  probe.frame = `${message.frame_w}x${message.frame_h}`;
  add('model', message.latency_ms);
  if (typeof message.capture_ms !== 'number') {
    return;
  }
  const rtt = Date.now() - message.capture_ms;
  add('pose_rtt', rtt);
  if (typeof message.pod_ms === 'number') {
    add('pod', message.pod_ms);
    add('network', rtt - message.pod_ms);
  }
}

/**
 * The id to send with an evaluation carrying this pose (`remote` from `detect()`), or undefined: only the
 * first send of each captured frame is timed, since the hook's tick re-sends the newest pose.
 */
export function onEvaluationSent(remote) {
  if (
    !probe ||
    typeof remote?.captureMs !== 'number' ||
    remote.captureMs === probe.lastSentCapture
  ) {
    return undefined;
  }
  probe.lastSentCapture = remote.captureMs;
  const sentAt = Date.now();
  if (typeof remote.receivedWallMs === 'number') {
    add('relay', sentAt - remote.receivedWallMs);
  }
  if (probe.pending.size >= MAX_PENDING) {
    probe.pending.delete(probe.pending.keys().next().value);
  }
  const id = probe.nextId++;
  probe.pending.set(id, { sentAt, captureMs: remote.captureMs });
  return `${ID_PREFIX}${id}`;
}

/** A raw message from the session socket: times the reply to a probed evaluation. */
export function onSocketMessage(text) {
  if (!probe || typeof text !== 'string') {
    return;
  }
  const match = REPLY_ID.exec(text);
  const sent = match && probe.pending.get(Number(match[1]));
  if (!sent) {
    return;
  }
  probe.pending.delete(Number(match[1]));
  const now = Date.now();
  add('ws_rtt', now - sent.sentAt);
  add('e2e', now - sent.captureMs);
}

/** The summary posted at the end, or null without a probe. */
export function probeSummary() {
  if (!probe) {
    return null;
  }
  const durationS = (Date.now() - probe.startedAt) / 1000;
  const summary = {
    transport: 'livekit',
    duration_s: Math.round(durationS),
    results: probe.results,
    heartbeats: probe.heartbeats,
    result_fps: durationS > 0 ? Math.round((probe.results / durationS) * 10) / 10 : 0,
    frame: probe.frame,
    mobile: navigator.userAgentData?.mobile ?? /Mobi|Android/i.test(navigator.userAgent),
    ua: navigator.userAgent.slice(0, 120),
    startup: probe.startup,
  };
  for (const name of SERIES) {
    const values = spread(probe.series[name]);
    if (values) {
      summary[name] = values;
    }
  }
  return summary;
}

/** End the probe and post its summary; a failed post is logged, never thrown. */
export function finishProbe() {
  const summary = probeSummary();
  const sessionId = probe?.sessionId;
  const sessionToken = probe?.sessionToken;
  probe = null;
  if (typeof window !== 'undefined') {
    window.removeEventListener('pagehide', finishProbe);
  }
  if (!summary || !sessionId) {
    return Promise.resolve();
  }
  // eslint-disable-next-line no-console
  console.info('[pose-latency]', summary);
  return postLatencySummary(sessionId, sessionToken, summary).catch(error =>
    console.warn('[pose-latency] summary not sent', error)
  );
}
